/**
 * The billing banner (mission 9 WP14 T085; sheet 51/7, 51/8; FR-015, FR-023, NFR-010; B.1; spec
 * amendment 13; analysis run 2, I12; squad U12, U29; contracts/ui-api.md "BillingBanner").
 *
 * Mounted once in the shell above every app page, from the credits read's `billing` member (the
 * chip's own observer of `['credits']`, so no extra read):
 * - dunning: a warning band (`role="status"`) with `dunningBanner`, the days left and the date
 *   read-only begins, and Update card;
 * - read-only: a blocked band (`role="alert"`) with its reason's sentence and its remedy, Update card
 *   for `update-payment`, Subscribe for `subscribe`;
 * - nothing while active, pending (AuthGate's), before the credits read first has data, and on
 *   `/app/settings/billing`, where `DunningBand` speaks.
 *
 * A live region is announced when its text changes, so the text is taken from a snapshot that moves
 * only when the state, the reason or the dunning day does (amendment 13, I12): a same-state refetch
 * changes nothing on screen, and a new day is announced once. Never dismissible (§7.5). The band-depth
 * rule, the banner plus at most one page band, holds because each page keeps one band slot (the
 * estimate panel's ProblemBand replaces its own band).
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useLocation } from 'react-router-dom'

import { Band, type BandKind } from '@/components/trellis/Band'
import type { BillingAccess } from '@/lib/api/billing'
import { date } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { creditsQuery } from '@/lib/queries'

/** Where the shell banner is suppressed: the billing page's DunningBand owns the message (50/10). */
const BILLING_PAGE = '/app/settings/billing'

interface BannerView {
  kind: BandKind
  text: string
  action: { label: string; to: string }
}

const UPDATE_CARD = { label: copyText('cardUpdate'), to: `${BILLING_PAGE}#card` }
const SUBSCRIBE = { label: copyText('readOnlyRemedySubscribe'), to: '/app/subscribe' }

/** The banner for one access member, or none. */
function bannerView(billing: BillingAccess | undefined): BannerView | null {
  if (billing?.state === 'dunning' && billing.dunning_days_left !== null && billing.read_only_at !== null) {
    const text = PROVISIONAL_COPY.dunningBanner.render({ days: billing.dunning_days_left, date: date(billing.read_only_at) })
    return { kind: 'warning', text, action: UPDATE_CARD }
  }
  if (billing?.state === 'read_only') {
    const action = billing.remedy === 'subscribe' ? SUBSCRIBE : UPDATE_CARD
    if (billing.reason === 'dunning') return { kind: 'blocked', text: copyText('readOnlyBannerDunning'), action }
    const ended = billing.read_only_since ?? billing.period_end
    if (ended !== null) return { kind: 'blocked', text: PROVISIONAL_COPY.readOnlyBannerCancelled.render({ date: date(ended) }), action }
  }
  return null
}

/**
 * Mission 10 WP18 cycle 2 (§7.5; U31): whether the banner shows a band for this access member, the same
 * predicate it renders from, so sheets 30 and 31 count its band exactly as it is drawn.
 */
export function billingBandShown(billing: BillingAccess | undefined): boolean {
  return bannerView(billing) !== null
}

/** What the announcement is keyed on (amendment 13; I12): the state, the reason and the dunning day. */
function announcementKey(billing: BillingAccess | undefined): string {
  return billing === undefined ? 'active' : `${billing.state}|${billing.reason ?? ''}|${billing.dunning_day ?? ''}`
}

export function BillingBanner() {
  const { pathname } = useLocation()
  const { data } = useQuery(creditsQuery())
  const [snapshot, setSnapshot] = useState<{ key: string; view: BannerView | null } | null>(null)

  // Derived state, set during render (as EstimatePanel's hold): the view moves only with its key.
  const key = announcementKey(data?.billing)
  if (data !== undefined && snapshot?.key !== key) setSnapshot({ key, view: bannerView(data.billing) })

  const view = snapshot?.view
  if (!view || pathname === BILLING_PAGE || pathname.startsWith(`${BILLING_PAGE}/`)) return null
  return (
    <Band
      kind={view.kind}
      requirement="B.1"
      className="mb-6"
      action={
        <Link to={view.action.to} className="text-body-sm text-[var(--color-link)] underline hover:text-[var(--color-link-hover)]">
          {view.action.label}
        </Link>
      }
    >
      <p data-billing-banner-text="">{view.text}</p>
    </Band>
  )
}
